import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { Profil } from './types';

/**
 * FAQ interne (TF-483) : ce que chacun lit (articles publiés qui visent son
 * profil), la recherche et le compte par rubrique viennent de `/api/faq`. La
 * direction reçoit en plus `gestion` : statut et profils de chaque article,
 * listes du formulaire et bornes. L'écran affiche.
 */

export type RubriqueFaq = 'PLATEFORME' | 'FORMATION' | 'EMARGEMENT' | 'ADMINISTRATIF' | 'FORMATEURS' | 'CONTACTS';
export type PublicFaq = 'APPRENANTS' | 'SALARIES' | 'TOUS';

export interface ArticleFaq {
  id: string;
  rubrique: RubriqueFaq;
  libelleRubrique: string;
  question: string;
  reponse: string;
  misAJourLe: string;
  gestion: {
    statut: 'BROUILLON' | 'PUBLIE';
    publieLe: string | null;
    profils: Profil[];
    publics: string;
    creeLe: string;
    creePar: string | null;
    modifiePar: string | null;
  } | null;
}

export interface GestionFaq {
  brouillons: number;
  vuPar: Profil | null;
  rubriques: { code: RubriqueFaq; libelle: string }[];
  profils: { code: Profil; libelle: string }[];
  publics: { code: PublicFaq; libelle: string; profils: Profil[] }[];
  limites: {
    question: { min: number; max: number };
    reponse: { min: number; max: number };
    recherche: number;
  };
}

export interface VueFaq {
  total: number;
  rubriques: { code: RubriqueFaq; libelle: string; articles: number }[];
  articles: ArticleFaq[];
  gestion: GestionFaq | null;
}

export interface FiltresFaq {
  recherche: string;
  rubrique: RubriqueFaq | '';
  vuPar: Profil | '';
}

export interface SaisieArticleFaq {
  rubrique: RubriqueFaq;
  question: string;
  reponse: string;
  profils: Profil[];
}

export function useFaq(filtres: FiltresFaq) {
  const chaine = new URLSearchParams(
    Object.entries(filtres).filter(([, valeur]) => valeur !== ''),
  ).toString();
  return useQuery({
    queryKey: ['faq', chaine],
    queryFn: () => appelApi<VueFaq>(`/faq${chaine ? `?${chaine}` : ''}`),
    placeholderData: keepPreviousData,
  });
}

function useGesteFaq<E>(appel: (entree: E) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: appel,
    onSuccess: () => void client.invalidateQueries({ queryKey: ['faq'] }),
  });
}

export function useCreerArticleFaq() {
  return useGesteFaq((saisie: SaisieArticleFaq & { publier: boolean }) =>
    appelApi<ArticleFaq>('/faq', { methode: 'POST', corps: saisie }),
  );
}

export function useModifierArticleFaq() {
  return useGesteFaq(({ id, ...saisie }: SaisieArticleFaq & { id: string }) =>
    appelApi<ArticleFaq>(`/faq/${encodeURIComponent(id)}`, { methode: 'PUT', corps: saisie }),
  );
}

export function usePublierArticleFaq() {
  return useGesteFaq(({ id, publier }: { id: string; publier: boolean }) =>
    appelApi<ArticleFaq>(`/faq/${encodeURIComponent(id)}/${publier ? 'publication' : 'retrait'}`, { methode: 'POST' }),
  );
}

export function useSupprimerArticleFaq() {
  return useGesteFaq((id: string) => appelApi<void>(`/faq/${encodeURIComponent(id)}`, { methode: 'DELETE' }));
}
